import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Paper,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import axios from "axios";

import config from "../../config";
import { HPC_COLORS } from "../hpc/hpcTheme";
import { saveBlob } from "./shared/paymentUtils";
import { TebaReceiptView } from "./tebaPaymentUi";

// The receipt itself is always shown on white paper, whatever the app theme is.
const paperTheme = createTheme({ palette: { mode: "light" } });

/**
 * Public, unauthenticated TEBA receipt page, resolved only by the signed token in the URL.
 * The server never returns a phone number for this page.
 */
const PublicTebaReceipt = () => {
  const { token } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const base = `${config.apiBaseUrl}/teba/receipts/public/${encodeURIComponent(token || "")}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(base);
      setReceipt(response.data.receipt || null);
    } catch (err) {
      setReceipt(null);
      setError(err.response?.status === 404 ? "This receipt link is not valid." : "Could not load the receipt. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const response = await axios.get(`${base}/pdf`, { responseType: "blob" });
      saveBlob(response.data, `${receipt?.receipt_number || "TEBA-receipt"}.pdf`);
    } catch {
      setError("Could not download the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <ThemeProvider theme={paperTheme}>
      <CssBaseline />
      <style>{"@media print{.teba-no-print{display:none !important}body{background:#fff !important}"
        + ".teba-scroll{height:auto !important;overflow:visible !important;background:#fff !important}}"}</style>
      {/* The page body cannot scroll (app.css), so this page is its own scroll area, like HpcSuccess. */}
      <Box
        className="teba-scroll"
        sx={{
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          background: `linear-gradient(180deg, ${HPC_COLORS.navy} 0%, ${HPC_COLORS.navyMid} 100%)`,
        }}
      >
        <Box sx={{ maxWidth: 720, mx: "auto", py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
          {loading ? (
            <Box sx={{ textAlign: "center", pt: 8 }}><CircularProgress sx={{ color: HPC_COLORS.hpuOrange }} /></Box>
          ) : error && !receipt ? (
            <Alert severity="error" action={<Button color="inherit" startIcon={<ReplayIcon />} onClick={load}>Retry</Button>}>{error}</Alert>
          ) : receipt && (
            <>
              <Box
                className="teba-no-print"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                  flexWrap: "wrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  py: 1,
                  backgroundColor: "rgba(10, 22, 40, 0.92)",
                  borderRadius: 1,
                }}
              >
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}>
                  Print
                </Button>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadPdf} disabled={pdfBusy}
                  sx={{ backgroundColor: HPC_COLORS.hpuOrange, color: "#1a1200", "&:hover": { backgroundColor: "#ffb84d" } }}>
                  {pdfBusy ? "Preparing…" : "Download PDF"}
                </Button>
              </Box>
              {error && <Alert severity="error" className="teba-no-print" sx={{ mb: 2 }}>{error}</Alert>}
              <Paper elevation={6} sx={{ p: { xs: 2, sm: 4 }, borderTop: `4px solid ${HPC_COLORS.hpuOrange}` }}>
                <TebaReceiptView receipt={receipt} />
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PublicTebaReceipt;
